import type { DocumentExtractionV2Invocation } from '@lifeomic/react-client';
import FhirService from '@lifeomic/phc-web-toolkit/src/services/FhirService';
import type { ServiceAuth } from '@lifeomic/phc-web-toolkit/src/hooks/useService/useService';
import {
  getSourceReference,
  isTaskForSource,
  selectSourceTask,
  type DocumentExtractionV2SourceResourceType,
} from './documentExtractionV2Source';
import { isDocumentExtractionV2InFlight } from './DocumentExtractionV2Tasks';
import {
  DOCUMENT_EXTRACTION_V2_SUMMARY_DOCUMENT_TYPE,
  DOCUMENT_EXTRACTION_V2_SUMMARY_SOURCE_TYPE,
  FHIR_DOCUMENT_TYPE_TAG_SYSTEM,
  FHIR_SOURCE_TYPE_TAG_SYSTEM,
} from './memberDocumentSource';

const documentReferenceFhirService = new FhirService('DocumentReference');

/**
 * Upper bound on summary DocumentReferences fetched per member when resolving
 * durable extraction state. The member documents table pages at 200 rows, so a
 * single page can never reference more sources than this.
 */
export const DOCUMENT_EXTRACTION_SUMMARY_SEARCH_COUNT = 1000;

/** Page size for a single-source summary lookup (relatesto-scoped). */
export const DOCUMENT_EXTRACTION_SOURCE_SUMMARY_SEARCH_COUNT = 100;

const SUMMARY_SORT_EPOCH = Number.NEGATIVE_INFINITY;

const summaryLastUpdatedMs = (summary: fhir.DocumentReference): number => {
  const lastUpdated = summary.meta?.lastUpdated;
  if (!lastUpdated) {
    return SUMMARY_SORT_EPOCH;
  }
  const parsed = Date.parse(lastUpdated);
  return Number.isNaN(parsed) ? SUMMARY_SORT_EPOCH : parsed;
};

export const getDocumentExtractionTask = (
  tasks: DocumentExtractionV2Invocation[],
  documentReferenceId: string,
) => {
  const sourceTasks = tasks.filter(task =>
    isTaskForSource(task, 'DocumentReference', documentReferenceId),
  );

  return (
    sourceTasks.find(
      task =>
        isDocumentExtractionV2InFlight(task) && task.status === 'processing',
    ) ??
    sourceTasks.find(isDocumentExtractionV2InFlight) ??
    selectSourceTask(tasks, 'DocumentReference', documentReferenceId)
  );
};

/**
 * Source DocumentReference ids a Document Extraction V2 summary was derived
 * from. The summary DocumentReference records each source it transformed via
 * `relatesTo[].target.reference` (e.g. `DocumentReference/<id>`), which is the
 * durable, task-independent link we use to mark a source document as extracted.
 */
export const getSummarySourceDocumentReferenceIds = (
  summary: fhir.DocumentReference,
): string[] =>
  (summary.relatesTo ?? [])
    .map(relation => relation.target?.reference)
    .filter(
      (reference): reference is string =>
        !!reference && reference.startsWith('DocumentReference/'),
    )
    .map(reference => reference.replace(/^DocumentReference\//, ''));

/**
 * Resolve which of `documentReferenceIds` have a persisted Document Extraction
 * V2 summary by reading the member's summary DocumentReferences directly.
 *
 * This intentionally does NOT reconstruct deterministic artifact ids (the
 * backend now suffixes the artifact identity with a non-reconstructable
 * `extraction_key`) and does NOT depend on the async task record (tasks expire
 * out of the visible task list). The summary DocumentReference, written for
 * every completed extraction, is the authoritative durable signal.
 */
export const loadDurableExtractedDocumentIds = async ({
  accountId,
  documentReferenceIds,
  memberId,
  projectId,
  serviceAuth,
}: {
  accountId: string;
  documentReferenceIds: string[];
  memberId: string;
  projectId: string;
  serviceAuth: ServiceAuth;
}) => {
  if (!documentReferenceIds.length) {
    return [];
  }

  const requestedDocumentReferenceIds = new Set(documentReferenceIds);

  try {
    const response = await documentReferenceFhirService.search(serviceAuth)(
      accountId,
      projectId,
      {
        subject: `Patient/${memberId}`,
        _tag: [
          `${FHIR_SOURCE_TYPE_TAG_SYSTEM}|${DOCUMENT_EXTRACTION_V2_SUMMARY_SOURCE_TYPE}`,
        ],
        _count: DOCUMENT_EXTRACTION_SUMMARY_SEARCH_COUNT,
      },
    );

    const extractedDocumentIds = new Set<string>();
    for (const entry of response.data.entry ?? []) {
      if (entry.resource?.resourceType !== 'DocumentReference') {
        continue;
      }
      for (const sourceDocumentReferenceId of getSummarySourceDocumentReferenceIds(
        entry.resource,
      )) {
        if (requestedDocumentReferenceIds.has(sourceDocumentReferenceId)) {
          extractedDocumentIds.add(sourceDocumentReferenceId);
        }
      }
    }

    return Array.from(extractedDocumentIds);
  } catch {
    return [];
  }
};

/**
 * Load the current Document Extraction V2 summary DocumentReference for one
 * source by searching FHIR (`relatesto` + current status), not by reconstructing
 * the artifact id.
 *
 * Summary artifact ids are hashed with a non-reconstructable `extraction_key`,
 * and async task rows (which carry the real summary id in `fhir_artifacts`) TTL
 * out after ~2 days. Without this search, the extraction review modal 404s on
 * reconstructed ids and looks empty even though a durable summary exists — the
 * same path Zori/Pulse already use to resolve contexts.
 */
export const findCurrentDocumentExtractionV2SummaryForSource = async ({
  accountId,
  projectId,
  subjectId,
  sourceResourceType,
  sourceResourceId,
  serviceAuth,
}: {
  accountId: string;
  projectId: string;
  subjectId: string;
  sourceResourceType: DocumentExtractionV2SourceResourceType;
  sourceResourceId: string;
  serviceAuth: ServiceAuth;
}): Promise<fhir.DocumentReference | null> => {
  const sourceReference = getSourceReference(
    sourceResourceType,
    sourceResourceId,
  );

  try {
    const response = await documentReferenceFhirService.search(serviceAuth)(
      accountId,
      projectId,
      {
        subject: `Patient/${subjectId}`,
        status: 'current',
        relatesto: sourceReference,
        _tag: [
          `${FHIR_SOURCE_TYPE_TAG_SYSTEM}|${DOCUMENT_EXTRACTION_V2_SUMMARY_SOURCE_TYPE}`,
          `${FHIR_DOCUMENT_TYPE_TAG_SYSTEM}|${DOCUMENT_EXTRACTION_V2_SUMMARY_DOCUMENT_TYPE}`,
        ],
        _count: DOCUMENT_EXTRACTION_SOURCE_SUMMARY_SEARCH_COUNT,
      },
    );

    const matchingSummaries = (response.data.entry ?? [])
      .map(entry => entry.resource)
      .filter(
        (resource): resource is fhir.DocumentReference =>
          resource?.resourceType === 'DocumentReference' &&
          (resource.relatesTo ?? []).some(
            relation => relation.target?.reference === sourceReference,
          ),
      )
      .sort(
        (left, right) =>
          summaryLastUpdatedMs(right) - summaryLastUpdatedMs(left),
      );

    return matchingSummaries[0] ?? null;
  } catch {
    return null;
  }
};
